/**
 * Disabilities Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 *
 * @version 2025.1.0
 */
import React from 'react';
import { InlineFilterBar, InlineSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { DISABILITY_TYPE_OPTIONS, SEVERITY_LEVEL_OPTIONS } from '../../../types/disability';

export interface DisabilitiesFiltersData {
  search: string;
  disabilityType: string;
  severity: string;
  isActive: string;
}

interface DisabilitiesFiltersProps {
  onFilter: (filters: DisabilitiesFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const TYPE_OPTIONS = DISABILITY_TYPE_OPTIONS.map((type) => ({ value: type, label: type }));
const SEVERITY_OPTIONS = SEVERITY_LEVEL_OPTIONS.map((level) => ({ value: level, label: level }));
const STATUS_OPTIONS = [
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Inativos' },
];

const defaultValues: DisabilitiesFiltersData = {
  search: '',
  disabilityType: '',
  severity: '',
  isActive: '',
};

export const DisabilitiesFiltersComponent: React.FC<DisabilitiesFiltersProps> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      disabilityType: 'Tipo',
      severity: 'Severidade',
      isActive: 'Status',
    },
    formatValue: {
      isActive: (v: string) => (v === 'true' ? 'Ativos' : 'Inativos'),
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Funcionário, empresa, descrição..."
      searchValue={filters.search}
      onSearchChange={(v) => setFilter('search', v)}
      resultCount={total}
      resultNoun="deficiências"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Tipo de Deficiência"
        value={filters.disabilityType}
        options={TYPE_OPTIONS}
        onChange={(v) => setFilter('disabilityType', v)}
        emptyLabel="Todos"
        minWidth={180}
      />
      <InlineSelect
        label="Severidade"
        value={filters.severity}
        options={SEVERITY_OPTIONS}
        onChange={(v) => setFilter('severity', v)}
        emptyLabel="Todas"
        minWidth={150}
      />
      <InlineSelect
        label="Status"
        value={filters.isActive}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('isActive', v)}
        emptyLabel="Todos"
        minWidth={130}
      />
    </InlineFilterBar>
  );
};

export default DisabilitiesFiltersComponent;
